import { type ReactNode, useRef } from "react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import "./styles.scss";

// ── Public types ────────────────────────────────────────────────

export interface TreeNode {
  id: string;
  label: string;
  type: string;
  icon?: ReactNode;
  /** A second line under the label, e.g. a note's date and campaign (MPN-1640).
   *  Optional: every other tree passes nothing and renders exactly as before. */
  meta?: ReactNode;
  children?: TreeNode[];
  data?: any;
}

export interface MenuAction {
  label: string;
  onClick: (node: TreeNode) => void;
  variant?: "default" | "danger";
  dividerBefore?: boolean;
}

export interface TreeNodeTypeConfig {
  icon?: ReactNode;
  menuActions?: (node: TreeNode) => MenuAction[];
}

export interface TreeNavigatorProps {
  nodes: TreeNode[];
  selectedId: string | null;
  onSelect: (node: TreeNode) => void;
  expandedIds: Set<string>;
  onToggle: (nodeId: string) => void;
  typeConfig?: Record<string, TreeNodeTypeConfig>;
  renderActions?: (node: TreeNode) => ReactNode;
  draggable?: boolean;
  onNodeDrop?: (dragNodeId: string, dropNodeId: string | null) => void;
  className?: string;
}

// ── Chevron icons ───────────────────────────────────────────────

const ChevronRight = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronDown = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Module-level drag state (shared across all rows) ───────────

let draggedNodeId: string | null = null;

// ── Single node ─────────────────────────────────────────────────

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (node: TreeNode) => void;
  expandedIds: Set<string>;
  onToggle: (nodeId: string) => void;
  typeConfig?: Record<string, TreeNodeTypeConfig>;
  renderActions?: (node: TreeNode) => ReactNode;
  draggable?: boolean;
  onNodeDrop?: (dragNodeId: string, dropNodeId: string | null) => void;
}

function TreeNodeRow({ node, depth, selectedId, onSelect, expandedIds, onToggle, typeConfig, renderActions, draggable, onNodeDrop }: TreeNodeRowProps) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const config = typeConfig?.[node.type];
  const icon = node.icon ?? config?.icon;
  const menuActions = config?.menuActions?.(node) ?? [];
  const dropIndicatorRef = useRef(false);

  const handleClick = () => {
    onSelect(node);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(node);
    }
  };

  const handleCaretClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  // ── Drag & drop handlers ──────────────────────────────────────

  const handleDragStart = (e: React.DragEvent) => {
    draggedNodeId = node.id;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!draggedNodeId || draggedNodeId === node.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dropIndicatorRef.current) {
      dropIndicatorRef.current = true;
      (e.currentTarget as HTMLElement).classList.add("tn-node--drop-target");
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    dropIndicatorRef.current = false;
    (e.currentTarget as HTMLElement).classList.remove("tn-node--drop-target");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove("tn-node--drop-target");
    dropIndicatorRef.current = false;
    if (draggedNodeId && draggedNodeId !== node.id && onNodeDrop) {
      onNodeDrop(draggedNodeId, node.id);
    }
    draggedNodeId = null;
  };

  const handleDragEnd = () => {
    draggedNodeId = null;
  };

  const dragProps = draggable ? {
    draggable: true,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onDragEnd: handleDragEnd,
  } : {};

  // ── Row rendering ─────────────────────────────────────────────

  const hasActions = !!renderActions;

  const row = hasActions ? (
    <div
      role="button"
      tabIndex={0}
      className={`tn-node ${isSelected ? "tn-node--selected" : ""}`}
      style={{ paddingLeft: `${16 + depth * 16}px` }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...dragProps}
    >
      <span className="tn-node-caret" onClick={hasChildren ? handleCaretClick : undefined}>
        {hasChildren ? (isExpanded ? <ChevronDown /> : <ChevronRight />) : <span style={{ width: 12 }} />}
      </span>
      {icon && <span className="tn-node-icon">{icon}</span>}
      <span className="tn-node-label">
        {node.label}
        {node.meta && <span className="tn-node-meta">{node.meta}</span>}
      </span>
      {renderActions(node)}
    </div>
  ) : (
    <button
      type="button"
      className={`tn-node ${isSelected ? "tn-node--selected" : ""}`}
      style={{ paddingLeft: `${16 + depth * 16}px` }}
      onClick={handleClick}
      {...dragProps}
    >
      <span className="tn-node-caret" onClick={hasChildren ? handleCaretClick : undefined}>
        {hasChildren ? (isExpanded ? <ChevronDown /> : <ChevronRight />) : <span style={{ width: 12 }} />}
      </span>
      {icon && <span className="tn-node-icon">{icon}</span>}
      <span className="tn-node-label">
        {node.label}
        {node.meta && <span className="tn-node-meta">{node.meta}</span>}
      </span>
    </button>
  );

  const children = hasChildren && isExpanded && (
    <div className="tn-node-children">
      {node.children!.map((child) => (
        <TreeNodeRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          onToggle={onToggle}
          typeConfig={typeConfig}
          renderActions={renderActions}
          draggable={draggable}
          onNodeDrop={onNodeDrop}
        />
      ))}
    </div>
  );

  if (menuActions.length === 0) {
    return (
      <>
        {row}
        {children}
      </>
    );
  }

  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          {row}
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content className="tn-context-menu">
            {menuActions.map((action, i) => (
              <div key={i}>
                {action.dividerBefore && <ContextMenu.Separator className="tn-context-sep" />}
                <ContextMenu.Item
                  className={`tn-context-item ${action.variant === "danger" ? "tn-context-item--danger" : ""}`}
                  onSelect={() => action.onClick(node)}
                >
                  {action.label}
                </ContextMenu.Item>
              </div>
            ))}
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>
      {children}
    </>
  );
}

// ── TreeNavigator ───────────────────────────────────────────────

export default function TreeNavigator({
  nodes,
  selectedId,
  onSelect,
  expandedIds,
  onToggle,
  typeConfig,
  renderActions,
  draggable,
  onNodeDrop,
  className,
}: TreeNavigatorProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const handleRootDragOver = (e: React.DragEvent) => {
    if (!draggable || !draggedNodeId) return;
    // Only highlight if the drag is directly over the root, not a child node
    if (e.target === rootRef.current) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      rootRef.current?.classList.add("tn-root--drop-target");
    }
  };

  const handleRootDragLeave = (e: React.DragEvent) => {
    if (e.target === rootRef.current) {
      rootRef.current?.classList.remove("tn-root--drop-target");
    }
  };

  const handleRootDrop = (e: React.DragEvent) => {
    rootRef.current?.classList.remove("tn-root--drop-target");
    if (!draggable || !draggedNodeId || !onNodeDrop) return;
    e.preventDefault();
    onNodeDrop(draggedNodeId, null);
    draggedNodeId = null;
  };

  return (
    <div
      ref={rootRef}
      className={`tn-root ${className ?? ""}`.trim()}
      onDragOver={handleRootDragOver}
      onDragLeave={handleRootDragLeave}
      onDrop={handleRootDrop}
    >
      {nodes.map((node) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          onToggle={onToggle}
          typeConfig={typeConfig}
          renderActions={renderActions}
          draggable={draggable}
          onNodeDrop={onNodeDrop}
        />
      ))}
    </div>
  );
}
